import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DisputedScoresIds,
  FilterValueDTO,
  IFilterDTO,
  IScoresOfWellDropsDTO,
  ISearchDisplayFieldDTO,
  SearchConstraintDTO,
  SearchDisplayFieldStatus,
  SearchNodeDTO,
  SearchTargetDTO,
  WellIngDTO,
} from 'business/models/Models';
import { PaginatedList, Pagination } from 'business/models/pagination';
import {
  saveSearchAction,
  searchResultThunk,
  fetchSearchDetailsAction,
  fetchWellDropsAction,
  fetchScoresAction,
  searchFiltersThunk,
  searchResultFilterValuesThunk,
  searchRowsCountThunk,
  scoreWellsThunk,
  searchChartDataThunk,
} from 'redux/thunks/searchnFilter';
import { RMRectangle, ScoringWell } from 'business/models/CustomModels';
import {
  convertFromIC,
  RuleGroupType,
  RuleGroupTypeIC,
  formatQuery,
  convertToIC,
} from 'react-querybuilder';
import { DEFAULT_PAGE_SIZE, IPaginatedList } from 'components/Pagination/constans';
import {
  FiltersState,
  MIN_FILTER_VALUES,
} from 'components/sidePanel/searchAndFilter/constants';
import { showAlertSnackBar, sortArray } from 'utils/Global';
import {
  formatDateDisplay,
  getFilterValueDTO,
} from 'components/sidePanel/searchAndFilter/utils';
import { cloneDeep } from 'lodash';
import { INTRESTINGSCOREID } from './plateReducer';
import EScoreType from 'enums/EScoreType';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { TotalColumns } from 'components/canvas/constants';

type SearchTargetsState = {
  data: SearchTargetDTO[];
  isFetching: boolean;
  error: string; //Generic Error object yet to be introduced.
};

type SearchTargetRelationState = {
  data: { [key: string]: SearchTargetDTO[] };
  isFetching: boolean;
  error: string; //Generic Error object yet to be introduced.
};

const SearchTargetRelationInitialState: SearchTargetRelationState = {
  data: {},
  isFetching: false,
  error: null,
};

const SearchTargetRelationOldInitialState: SearchTargetRelationState = {
  data: {},
  isFetching: false,
  error: null,
};

const searchTargetsInitialState: SearchTargetsState = {
  data: [],
  isFetching: false,
  error: null,
};

const searchTargetsOldInitialState: SearchTargetsState = {
  data: [],
  isFetching: false,
  error: null,
};

type WellDropsState = {
  data: IPaginatedList<WellIngDTO>;
  isFetching: boolean;
  error: string; //Generic Error object yet to be introduced.
};

export type SearchDetailsState = {
  id: number;
  searchName: string;
  constraints: RuleGroupType;
  searchPaths: number[];
  isFetching: boolean;
  error: string;
  displayFields: ISearchDisplayFieldDTO[];
  selectedTargetId: number;
  isSavingSearch: boolean;
  saveSearchError: string;
  rootTreeNodeId?: number;
  selectDistinct: boolean;
  filters?: FiltersState;
  isDirty: boolean;
  screenHitSelectedScore: { [groupId: number]: number[] };
  allowEdits: boolean;
  searchNode?: SearchNodeDTO;
  errorCode?: number; // temp. we should have a geneic error object
  selectedChartId: number;
  accessRightName: string;
  hasIndependentConstraints: boolean;
};

const constraintsInitialState: RuleGroupType | SearchConstraintDTO = {
  combinator: 'and',
  rules: [],
};

const filtersInitialState: FiltersState = {
  distinctRows: true,
  enableFilters: true,
  primaryFilter: undefined,
  data: [],
};

const SearchDetailsInitialState: SearchDetailsState = {
  id: -1,
  selectedTargetId: null,
  searchName: '',
  constraints: constraintsInitialState,
  searchPaths: [],
  isFetching: false,
  displayFields: [],
  error: '',
  isSavingSearch: false,
  saveSearchError: '',
  rootTreeNodeId: null,
  selectDistinct: true,
  filters: filtersInitialState,
  isDirty: false,
  screenHitSelectedScore: {},
  allowEdits: true,
  errorCode: null,
  selectedChartId: -1,
  accessRightName: '',
  hasIndependentConstraints: false,
};

const wellDropsStateInitialState: WellDropsState = {
  data: new PaginatedList([], new Pagination(100, 1, 0)),
  isFetching: false,
  error: null,
};

type CanvasWellSelection = {
  selectedWellDetails: WellIngDTO[];
  selectedWellCoordinates: RMRectangle[];
};

const canvasWellSelectionInitialState: CanvasWellSelection = {
  selectedWellDetails: [],
  selectedWellCoordinates: [],
};

export type WellDropScoresState = {
  scores: { [key: string]: IScoresOfWellDropsDTO[] }; //[key: string] is dropid&batchid stitched by a "-"; e.g: {"dropId-bcatchId": scoresList[]}
  isFetching: boolean;
  error: string;
};

const wellDropScoresInitialState: WellDropScoresState = {
  scores: { '': [] },
  isFetching: false,
  error: '',
};

export type SearchnFilterDataStates = {
  searchTargets: SearchTargetsState;
  targetRelations: SearchTargetRelationState;
  wellDrops: WellDropsState;
  searchResult: SearchResultState;
  canvasWellSelection: CanvasWellSelection;
  scores: WellDropScoresState;
  searchTargetsOld: SearchTargetsState;
  targetRelationsOld: SearchTargetRelationState;
  charts: SearchChartData[];
};

export type SearchnFilter = SearchnFilterDataStates & {
  searchDetails: SearchDetailsState;
  searchDetailsOriginal: SearchDetailsState;
};

export type SearchResultState = {
  rows: IPaginatedList<{ [key: string]: any }>;
  selectedRowIndices: number[];
  isFetching: boolean;
  error: string;
  itemsOriginal: { [key: string]: any }[];
};

export type SearchChartData = {
  chartId: number;
  data: { [key: string]: any }[];
  isFetching: boolean;
  error?: string;
};

const searchResultInitialState: SearchResultState = {
  rows: { items: [], pagination: { page: 1, perPage: DEFAULT_PAGE_SIZE, total: 1 } },
  selectedRowIndices: [],
  isFetching: false,
  error: '',
  itemsOriginal: [],
};

const initialState: SearchnFilter = {
  searchTargets: searchTargetsInitialState,
  targetRelations: SearchTargetRelationInitialState,
  wellDrops: wellDropsStateInitialState,
  searchResult: searchResultInitialState,
  canvasWellSelection: canvasWellSelectionInitialState,
  searchDetails: SearchDetailsInitialState,
  searchDetailsOriginal: SearchDetailsInitialState,
  scores: wellDropScoresInitialState,
  searchTargetsOld: searchTargetsOldInitialState,
  targetRelationsOld: SearchTargetRelationOldInitialState,
  charts: [],
};

const SearchnFilterSlice = createSlice({
  name: 'searchnFilter',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(searchResultThunk.pending, (state) => {
      state.searchResult.isFetching = true;
      state.searchResult.error = '';
    });
    builder.addCase(searchResultThunk.fulfilled, (state, action) => {
      const columns: any[] = state.searchDetails.displayFields
        .filter((sf) => sf.status === SearchDisplayFieldStatus.Display)
        .map((sf) => sf.columnHeader);

      state.searchResult.itemsOriginal = action.payload.searchRows;
      state.searchResult.rows.items = state.searchDetails.filters.distinctRows
        ? state.searchResult.itemsOriginal.distinctByMultipleProperties(columns)
        : state.searchResult.itemsOriginal;
      state.searchResult.isFetching = false;
      state.searchResult.error = '';
      state.searchResult.selectedRowIndices = [0];
    });
    builder.addCase(searchResultThunk.rejected, (state, action) => {
      if (action.payload) {
        state.searchResult.error = action.payload as string;
      } else {
        state.searchResult.error = action.error.message;
      }
      state.searchResult.isFetching = false;
      state.searchResult.rows = searchResultInitialState.rows;
      state.searchResult.selectedRowIndices = [];
    });
    builder.addCase(fetchSearchDetailsAction.pending, (state) => {
      state.searchDetails.isFetching = true;
      state.searchDetails.error = '';
    });
    builder.addCase(fetchSearchDetailsAction.fulfilled, (state, action) => {
      const {
        constraints,
        searchTargetId,
        id,
        searchPathTreeNodeIds,
        node,
        displayFields,
        rootTreeNodeId,
        selectDistinct,
        selectedChartId,
        accessRightName,
        hasIndependentConstraints,
      } = action.payload;
      state.searchDetails = {
        ...state.searchDetails,
        searchPaths: searchPathTreeNodeIds,
        searchName: node.name,
        allowEdits: node.allowEdits,
        selectedTargetId: searchTargetId,
        displayFields,
        isFetching: false,
        rootTreeNodeId,
        selectDistinct,
        hasIndependentConstraints,
        //@ts-ignore
        constraints: hasIndependentConstraints
          ? convertToIC(constraints as RuleGroupType)
          : constraints, //todo: need to fix the type. need to handle combinator type.
        id,
        error: '',
        isSavingSearch: false,
        saveSearchError: '',
        searchNode: node,
        selectedChartId: selectedChartId || -1,
        accessRightName: accessRightName,
      };
      state.searchDetailsOriginal = cloneDeep(state.searchDetails);
    });
    builder.addCase(fetchSearchDetailsAction.rejected, (state, action) => {
      const _payload = action.payload as Response;
      state.searchDetails = {
        ...SearchDetailsInitialState,
        error: _payload.statusText,
        errorCode: _payload.status,
      };
    });
    builder.addCase(saveSearchAction.pending, (state) => {
      state.searchDetails.isSavingSearch = true;
      state.searchDetails.saveSearchError = '';
    });
    builder.addCase(saveSearchAction.fulfilled, (state) => {
      showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Saved Successfully');
      state.searchDetails.isSavingSearch = false;
      state.searchDetails.saveSearchError = '';
      state.searchDetails.isDirty = false;
      state.searchDetails.filters.data.forEach((d) => {
        d.originalValues = [...d.values];
      });
      state.searchDetailsOriginal = cloneDeep(state.searchDetails);
    });
    builder.addCase(saveSearchAction.rejected, (state, action) => {
      if (action.payload?.['errorCode'] !== 404) {
        if (action.payload?.['errorCode'] == 409) {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            action.payload?.['message'],
          );
        } else {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            action.error.message,
          );
        }
      }
      state.searchDetails.isSavingSearch = false;
      state.searchDetails.saveSearchError = action.error.message;
    });
    builder.addCase(fetchWellDropsAction.pending, (state) => {
      state.wellDrops.isFetching = true;
      state.wellDrops.error = '';
    });
    builder.addCase(fetchWellDropsAction.fulfilled, (state, action) => {
      state.wellDrops.data = action.payload;
      state.wellDrops.isFetching = false;
      state.wellDrops.error = '';
    });
    builder.addCase(fetchWellDropsAction.rejected, (state, action) => {
      state.wellDrops.isFetching = false;
      state.wellDrops.error = action.error.message;
    });
    builder.addCase(fetchScoresAction.pending, (state) => {
      state.scores.isFetching = true;
      state.scores.error = '';
    });
    builder.addCase(fetchScoresAction.fulfilled, (state, action) => {
      state.scores.scores = action.payload;
      state.scores.isFetching = false;
      state.scores.error = '';
    });
    builder.addCase(fetchScoresAction.rejected, (state, action) => {
      state.scores.isFetching = false;
      state.scores.error = action.error.message;
    });
    /** Cases for Search Filters */
    builder.addCase(searchFiltersThunk.fulfilled, (state, action) => {
      state.searchDetails.filters.data = action.payload;
      state.searchDetails.filters.data.forEach((d) => {
        d.originalValues = [...d.values];
        d.selectedValues = sortArray(
          d.values?.map((f) => (f.value ? f.value.toString() : null)),
        );
      });
    });
    builder.addCase(searchResultFilterValuesThunk.pending, (state, action) => {
      const { searchFieldId } = action.meta.arg;
      const filterIndex = state.searchDetails.filters.data.findIndex(
        (f) => f.searchFieldId === searchFieldId,
      );
      state.searchDetails.filters.data[filterIndex].isFetching = true;
    });
    builder.addCase(searchResultFilterValuesThunk.fulfilled, (state, action) => {
      const { searchFieldId } = action.meta.arg;
      const obj: any = action.payload;
      const filterIndex = state.searchDetails.filters.data.findIndex(
        (f) => f.searchFieldId === searchFieldId,
      );

      if (filterIndex !== -1) {
        const filter = state.searchDetails.filters.data[filterIndex];
        filter.isFetching = false;
        const isDate = getFilterType(state, filter.searchFieldId)?.includes('Date');

        if (filter.id < 0 && obj.values.length <= MIN_FILTER_VALUES) {
          let newValues = obj.values.map((value: any) =>
            getFilterValueDTO(
              filter.id,
              value ? (isDate ? formatDateDisplay(value) : value.toString()) : null,
            ),
          );
          if (isDate) {
            newValues = newValues.sort((a, b) =>
              a.value === null
                ? -1
                : b.value === null
                ? 1
                : new Date(a.value).getTime() - new Date(b.value).getTime(),
            );
          }
          filter.values = [...newValues];
          filter.selectedValues = newValues.map((f) => f.value);
        }
        filter.allValues = obj.values;
      }
    });

    builder.addCase(searchRowsCountThunk.fulfilled, (state, action) => {
      state.searchResult.rows.pagination.total = action.payload.searchRows[0]['COUNT'];
    }),
      builder.addCase(searchRowsCountThunk.pending, (state, action) => {
        state.searchResult.rows.pagination.total = -1;
      });
    builder.addCase(scoreWellsThunk.fulfilled, (state, action) => {
      const scoringWells = action.payload;
      const { selectedWellDetails } = action.meta.arg;
      scoringWells.forEach((scoringWell) => {
        const dropId = selectedWellDetails.find(
          (w) => w.wellNum === scoringWell.wellNumber,
        )?.dropId;
        SearchnFilterSlice.caseReducers.applyScore(state, {
          type: 'applyScore',
          payload: { wellScore: scoringWell, dropId },
        });
      });
    });
    builder.addCase(searchChartDataThunk.pending, (state, action) => {
      const chart = state.charts.find((c) => c.chartId === action.meta.arg.chartId);
      if (chart) {
        chart.isFetching = true;
        chart.data = [];
        chart.error = '';
      } else {
        state.charts.push({
          chartId: action.meta.arg.chartId,
          data: [],
          isFetching: true,
        });
      }
    });
    builder.addCase(searchChartDataThunk.fulfilled, (state, action) => {
      const chart = state.charts.find((c) => c.chartId === action.meta.arg.chartId);
      if (chart) {
        chart.isFetching = false;
        chart.data = action.payload.searchRows;
        chart.error = '';
      } else {
        state.charts.push({
          chartId: action.meta.arg.chartId,
          data: action.payload.searchRows,
          isFetching: false,
        });
      }
    });
    builder.addCase(searchChartDataThunk.rejected, (state, action) => {
      const chart = state.charts.find((c) => c.chartId === action.meta.arg.chartId);
      if (chart) {
        chart.isFetching = true;
        chart.data = [];
        chart.error = action.payload as string;
      } else {
        state.charts.push({
          chartId: action.meta.arg.chartId,
          data: [],
          isFetching: true,
          error: action.payload as string,
        });
      }
    });
  },
  reducers: {
    setIsFetching: (
      state,
      action: PayloadAction<{
        value: boolean;
        stateKey: 'searchTargets' | 'targetRelations' | 'wellDrops';
      }>,
    ): void => {
      const { stateKey, value } = action.payload;
      state[stateKey].isFetching = value;
    },
    setSearchTargets: (state, action: PayloadAction<SearchTargetDTO[]>): void => {
      state.searchTargets.data = action.payload;
      state.searchTargets.isFetching = false;
    },
    setTargetRelations: (
      state,
      action: PayloadAction<{ [key: string]: number[] }>,
    ): void => {
      targetRelationActionUtil(action.payload, state);
    },
    setSelectedTargetId: (state, action: PayloadAction<number>): void => {
      state.searchDetails.selectedTargetId = action.payload;
      SetSerchDetailsDirty(state);
    },
    setSearchName: (state, action: PayloadAction<string>): void => {
      state.searchDetails.searchName = action.payload;
      SetSerchDetailsDirty(state);
    },
    setCurrentPage: (state, action: PayloadAction<number>): void => {
      state.searchResult.selectedRowIndices = [0];
      state.searchResult.rows.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>): void => {
      state.searchResult.selectedRowIndices = [0];
      state.searchResult.rows.pagination.page = 1;
      state.searchResult.rows.pagination.perPage = action.payload;
    },
    setSelectedRowIndices: (state, action: PayloadAction<number[]>): void => {
      state.searchResult.selectedRowIndices = action.payload;
    },
    setConstraints: (state, action: PayloadAction<RuleGroupType>): void => {
      state.searchDetails.constraints = action.payload;
      const nonIcConstraints = action.payload.combinator
        ? action.payload
        : convertFromIC(action.payload as RuleGroupTypeIC);
      if (
        JSON.stringify(formatQuery(nonIcConstraints, 'json_without_ids')) !=
        JSON.stringify(
          formatQuery(state.searchDetailsOriginal.constraints, 'json_without_ids'),
        )
      ) {
        SetSerchDetailsDirty(state);
      }
    },
    setSearchPaths: (state, action: PayloadAction<number[]>): void => {
      state.searchDetails.searchPaths = action.payload;
      SetSerchDetailsDirty(state);
    },
    updateWellSelection: (
      state,
      action: PayloadAction<CanvasWellSelection & { appendSelection?: boolean }>,
    ): void => {
      const {
        appendSelection = false,
        selectedWellDetails,
        selectedWellCoordinates,
      } = action.payload;

      if (appendSelection) {
        state.canvasWellSelection.selectedWellDetails.push(...selectedWellDetails);
        state.canvasWellSelection.selectedWellCoordinates.push(
          ...selectedWellCoordinates,
        );
      } else {
        state.canvasWellSelection.selectedWellDetails = selectedWellDetails;
        state.canvasWellSelection.selectedWellCoordinates = selectedWellCoordinates;
      }
    },
    removeDisplayField: (state, action: PayloadAction<number>): void => {
      if (
        state.searchDetails.displayFields[action.payload].status !==
        SearchDisplayFieldStatus.HiddenForIDs
      ) {
        state.searchDetails.displayFields.splice(action.payload, 1);
        SetSerchDetailsDirty(state);
      }
    },
    addDisplayField: (state, action: PayloadAction<ISearchDisplayFieldDTO>): void => {
      state.searchDetails.displayFields.push(action.payload);
      SetSerchDetailsDirty(state);
    },
    addDisplayFields: (state, action: PayloadAction<ISearchDisplayFieldDTO[]>): void => {
      const searchFieldIds = state.searchDetails.displayFields.map(
        (d) => d.searchFieldId,
      );
      const newFields = action.payload.filter(
        (d) => !searchFieldIds.includes(d.searchFieldId),
      );
      state.searchDetails.displayFields = [
        ...state.searchDetails.displayFields,
        ...newFields,
      ];
    },
    toggleDisplayField: (state, action: PayloadAction<number>): void => {
      const displayField = state.searchDetails.displayFields.find(
        (df) => df.searchFieldId === action.payload,
      );
      if (displayField.status === SearchDisplayFieldStatus.Display) {
        displayField.status = SearchDisplayFieldStatus.NotDisplay;
      } else {
        displayField.status = SearchDisplayFieldStatus.Display;
      }
      SetSerchDetailsDirty(state);
    },
    setDisplayFields: (state, action: PayloadAction<ISearchDisplayFieldDTO[]>): void => {
      state.searchDetails.displayFields = action.payload;
      SetSerchDetailsDirty(state);
    },
    setSerchDetailsDirty: (state, action): void => {
      state.searchDetails.isDirty = true;
    },
    discardSearchDetails: (state, action): void => {
      state.searchDetails = state.searchDetailsOriginal;
    },
    toggleSearchFilter: (state, action: PayloadAction<IFilterDTO>): void => {
      const filters = [...state.searchDetails.filters.data];
      const existingFilterIndex = filters?.findIndex(
        (filter) => filter.searchFieldId === action.payload.searchFieldId,
      );
      if (existingFilterIndex !== -1) {
        filters.splice(existingFilterIndex, 1);
      } else {
        filters.push(action.payload);
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    addSearchFilters: (state, action: PayloadAction<IFilterDTO[]>): void => {
      const searchFieldIds = state.searchDetails.filters.data.map(
        (filter) => filter.searchFieldId,
      );
      const newFilters = action.payload.filter(
        (newFilter) => !searchFieldIds.includes(newFilter.searchFieldId),
      );
      state.searchDetails.filters.data = [
        ...state.searchDetails.filters.data,
        ...newFilters,
      ];
      SetSerchDetailsDirty(state);
    },
    addSearchFilterValue: (state, action: PayloadAction<FilterValueDTO>): void => {
      const { filterId, value } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const valueExists = filter.values.some((v) => v.value === value);
        if (!valueExists) {
          filter.values = [...(filter.values || []), action.payload];
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    removeSearchFilterValue: (state, action: PayloadAction<FilterValueDTO>): void => {
      const { filterId, value } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];

        const valueExists = filter.values.some((v) => v.value === value);
        if (valueExists) {
          filter.values = filter.values.filter((v) => v.value !== value);
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    toggleSelectAllSearchFilterValues: (
      state,
      action: PayloadAction<{ filterId: number; selectAll: boolean; allValues: boolean }>,
    ): void => {
      const { filterId, selectAll, allValues } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const isDate = getFilterType(state, filter.searchFieldId)?.includes('Date');
        if (selectAll) {
          const valuesArr = allValues
            ? filter.allValues || []
            : filter.selectedValues || [];
          const missingValues = valuesArr.filter(
            (value) =>
              !filter.values.some((v) => v.value === (value ? value.toString() : null)),
          );
          const newFilters = missingValues.map((mv) =>
            getFilterValueDTO(
              filter.id,
              isDate ? (mv ? formatDateDisplay(mv) : null) : mv ? mv.toString() : null,
            ),
          );
          filter.values = [...filter.values, ...newFilters];
        } else {
          filter.values = [];
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    invertSearchFilterValues: (
      state,
      action: PayloadAction<{ filterId: number }>,
    ): void => {
      const { filterId } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const { selectedValues } = filter;
        if (selectedValues) {
          const missingValues = selectedValues.filter(
            (value) =>
              !filter.values.some((v) => v.value === (value ? value.toString() : null)),
          );
          const newFilters = missingValues?.map((mv) => {
            return getFilterValueDTO(filter.id, mv ? mv.toString() : null);
          });
          filter.values = newFilters;
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    addSearchFilterValues: (
      state,
      action: PayloadAction<{ filterId: number; values: any[] }>,
    ): void => {
      const { filterId, values } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const { allValues } = filter;
        if (allValues) {
          const newFilterValues = values.map((newValue) =>
            getFilterValueDTO(filter.id, newValue ? newValue.toString() : null),
          );
          filter.values = [...newFilterValues];
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    resetSearchFilterValues: (
      state,
      action: PayloadAction<{ filterId: number; values: FilterValueDTO[] }>,
    ): void => {
      const { filterId, values } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        filter.values = [...values];
        if (filter.selectedValues) {
          const uniqueValues =
            values
              .map((fv) => fv.value)
              .filter((value) => !filter.selectedValues.includes(value)) || [];
          filter.selectedValues = [...filter.selectedValues, ...uniqueValues];
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    resetSearchFilters: (state): void => {
      state.searchDetails.filters.data = [];
      SetSerchDetailsDirty(state);
    },
    toggleDistinctRows: (state): void => {
      state.searchDetails.filters.distinctRows =
        !state.searchDetails.filters.distinctRows;
      SetSerchDetailsDirty(state);

      const columns: any[] = state.searchDetails.displayFields
        .filter((sf) => sf.status === SearchDisplayFieldStatus.Display)
        .map((sf) => sf.columnHeader);

      state.searchResult.rows.items = state.searchDetails.filters.distinctRows
        ? state.searchResult.itemsOriginal.distinctByMultipleProperties(columns)
        : state.searchResult.itemsOriginal;
    },
    toggleEnableFilters: (state): void => {
      state.searchDetails.filters.enableFilters =
        !state.searchDetails.filters.enableFilters;
      SetSerchDetailsDirty(state);
    },
    addFilterSelectedValues: (
      state,
      action: PayloadAction<{ filterId: number; values: string[] }>,
    ): void => {
      const { filterId, values } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const selectedValues = filter.selectedValues || [];
        const uniqueValues = values.filter((value) => !selectedValues.includes(value));
        filter.selectedValues = sortArray([...selectedValues, ...uniqueValues]);
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    resetFilterSelectedValues: (
      state,
      action: PayloadAction<{ filterId: number; values: string[] }>,
    ): void => {
      const { filterId, values } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        filter.selectedValues = [...values];
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    removeFilterSelectedValues: (
      state,
      action: PayloadAction<{ filterId: number; values: string[] }>,
    ): void => {
      const { filterId, values } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const selectedValues = filter.selectedValues || [];
        filter.selectedValues = selectedValues.filter((v) => !values.includes(v));
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    toggleSelectAllSelectedValues: (
      state,
      action: PayloadAction<{ filterId: number; selectAll: boolean }>,
    ): void => {
      const { filterId, selectAll } = action.payload;
      const filters = [...state.searchDetails.filters.data];
      const filterIndex = filters.findIndex((f) => f.id === filterId);

      if (filterIndex !== -1) {
        const filter = filters[filterIndex];
        const isDate = getFilterType(state, filter.searchFieldId)?.includes('Date');
        if (selectAll) {
          filter.selectedValues = [
            ...filter.allValues.map((v) =>
              isDate ? formatDateDisplay(v) : v.toString(),
            ),
          ];
        } else {
          filter.selectedValues = [];
        }
      }
      state.searchDetails.filters.data = filters;
      SetSerchDetailsDirty(state);
    },
    selectAllSearchFilterValues: (state): void => {
      state.searchDetails.filters.data.forEach((filter) => {
        if (filter.selectedValues && filter.selectedValues.length > 0) {
          const existingValues = filter.values.map((v) => v.value);
          const uniqueSelectedValues = filter.selectedValues.filter(
            (value) => !existingValues.includes(value ? value.toString() : null),
          );
          const newFilterValues = uniqueSelectedValues.map((value) =>
            getFilterValueDTO(filter.id, value ? value.toString() : null),
          );
          filter.values = [...filter.values, ...newFilterValues];
        }
      });
      SetSerchDetailsDirty(state);
    },
    setSearchTargetsOldVersion: (
      state,
      action: PayloadAction<SearchTargetDTO[]>,
    ): void => {
      state.searchTargetsOld.data = action.payload;
      state.searchTargetsOld.isFetching = false;
    },
    setTargetRelationsOld: (
      state,
      action: PayloadAction<{ [key: string]: number[] }>,
    ): void => {
      targetRelationActionUtilOld(action.payload, state);
    },
    setPrimaryFilter: (state, action: PayloadAction<number>) => {
      state.searchDetails.filters.primaryFilter = action.payload;
    },
    setSelectedScores: (
      state,
      action: PayloadAction<{
        [groupId: number]: number[];
      }>,
    ): void => {
      state.searchDetails.screenHitSelectedScore = action.payload;
    },
    clearSearchFilters: (state) => {
      state.searchDetails.filters.data = [];
      SetSerchDetailsDirty(state);
    },
    applyScore: (
      state,
      action: PayloadAction<{ wellScore: ScoringWell; dropId: number }>,
    ) => {
      const { wellScore, dropId } = action.payload;
      const {
        wellNumber,
        wellDropScoreGroupId,
        imageBatchId,
        dropNumber,
        isDeleteScore,
        wellDropScoreId,
      } = wellScore;
      const currWellScores = state.scores.scores[`${dropId}-${imageBatchId}`];
      let allInspectionsScores = currWellScores?.length ? [...currWellScores] : [];
      const allScoreIndex = allInspectionsScores.findIndex(
        (wds) =>
          wds.wellNumber === wellNumber &&
          wds.wellDropScoreGroupId === wellDropScoreGroupId &&
          wds.scoreBatchId === imageBatchId &&
          wds.dropNumber === dropNumber,
      );

      const scoreObj = [
        {
          wellNumber,
          wellDropScoreId,
          wellDropScoreGroupId,
          isAutoScore: false,
          scoreBatchId: imageBatchId,
          dropNumber,
          scoreType: EScoreType.Manual,
        } as IScoresOfWellDropsDTO,
      ];

      if (isDeleteScore && wellDropScoreGroupId === INTRESTINGSCOREID) {
        allInspectionsScores = allInspectionsScores.filter(
          (scores) =>
            scores.wellDropScoreGroupId !== INTRESTINGSCOREID ||
            scores.dropNumber !== dropNumber,
        );
      } else if (isDeleteScore && allScoreIndex !== -1) {
        allInspectionsScores.splice(allScoreIndex, 1);
      } else if (!isDeleteScore && wellDropScoreGroupId === INTRESTINGSCOREID) {
        allInspectionsScores = [...allInspectionsScores, ...scoreObj];
      } else if (allScoreIndex === -1) {
        // If selected well have scores from other score groups but not from the scoregroup which we have scored so it will be added in that well scores
        allInspectionsScores.push(scoreObj[0]);
      } else if (allScoreIndex !== -1) {
        allInspectionsScores.splice(allScoreIndex, 1, scoreObj[0]);
      }

      state.scores.scores[`${dropId}-${imageBatchId}`] = allInspectionsScores;
    },
    moveNextSearchResultItem: (state): void => {
      const selectedRowIndices = state.searchResult.selectedRowIndices;
      state.searchResult.selectedRowIndices =
        state.searchResult.rows.pagination.total <= selectedRowIndices[0]
          ? [0]
          : [state.searchResult.selectedRowIndices[0] + 1];
    },
    markDisputedScores: (
      state,
      action: PayloadAction<{
        dropId: number;
        imageBatchId: number;
        disputedScoreGroupsIds: DisputedScoresIds[];
      }>,
    ) => {
      const { dropId, imageBatchId, disputedScoreGroupsIds } = action.payload;
      const currWellScores = state.scores.scores[`${dropId}-${imageBatchId}`];
      if (currWellScores) {
        disputedScoreGroupsIds.forEach((disputedScoreGroupIds) => {
          const updatedScores = currWellScores.map((score) => {
            if (
              score.imageBatchWellDropScoreId ===
              disputedScoreGroupIds.imageBatchWellDropScoreID
            ) {
              return { ...score, isDisputed: true };
            } else {
              return score;
            }
          });
          state.scores.scores[`${dropId}-${imageBatchId}`] = updatedScores;
        });
      }
    },
    clearSearchResult: (state) => {
      state.searchResult = initialState.searchResult;
    },
    navigateWellByKey: (state, action: PayloadAction<string>) => {
      const totalWells = state.searchResult.rows.items.length;
      const currentWellIndex = state.searchResult.selectedRowIndices[0];
      let nextWellIndex = currentWellIndex;
      if (action.payload === 'ArrowRight') {
        nextWellIndex = (currentWellIndex + 1) % totalWells;
      } else if (action.payload === 'ArrowLeft') {
        nextWellIndex = (currentWellIndex - 1 + totalWells) % totalWells;
      } else if (action.payload === 'ArrowDown') {
        nextWellIndex =
          currentWellIndex + TotalColumns >= totalWells
            ? (currentWellIndex + TotalColumns) % TotalColumns
            : currentWellIndex + TotalColumns;
      } else if (action.payload === 'ArrowUp') {
        nextWellIndex =
          currentWellIndex - TotalColumns <= 0
            ? Math.floor(totalWells / TotalColumns) * TotalColumns + currentWellIndex
            : currentWellIndex - TotalColumns;
        nextWellIndex =
          nextWellIndex >= totalWells ? nextWellIndex - TotalColumns : nextWellIndex;
      }
      if (nextWellIndex !== currentWellIndex) {
        state.searchResult.selectedRowIndices = [nextWellIndex];
      }
    },
    setSelectedChartId: (state, action: PayloadAction<number>) => {
      state.searchDetails.selectedChartId = action.payload;
      state.searchDetails.isDirty = true;
    },
    clearChartsData: (state) => {
      state.charts = [];
    },
    toggleHasIndependentConstraints: (state) => {
      state.searchDetails.hasIndependentConstraints =
        !state.searchDetails.hasIndependentConstraints;
      state.searchDetails.isDirty = true;
    },
  },
});

export const {
  setSearchTargets,
  setIsFetching,
  setTargetRelations,
  setSelectedTargetId,
  setSearchName,
  setCurrentPage,
  setPageSize,
  setSelectedRowIndices,
  updateWellSelection,
  setConstraints,
  setSearchPaths,
  removeDisplayField,
  addDisplayField,
  addDisplayFields,
  toggleDisplayField,
  setDisplayFields,
  toggleSearchFilter,
  addSearchFilters,
  addSearchFilterValue,
  removeSearchFilterValue,
  toggleSelectAllSearchFilterValues,
  invertSearchFilterValues,
  addSearchFilterValues,
  resetSearchFilterValues,
  resetSearchFilters,
  toggleDistinctRows,
  toggleEnableFilters,
  addFilterSelectedValues,
  resetFilterSelectedValues,
  removeFilterSelectedValues,
  toggleSelectAllSelectedValues,
  selectAllSearchFilterValues,
  discardSearchDetails,
  setSearchTargetsOldVersion,
  setTargetRelationsOld,
  setPrimaryFilter,
  setSelectedScores,
  clearSearchFilters,
  applyScore,
  moveNextSearchResultItem,
  markDisputedScores,
  clearSearchResult,
  navigateWellByKey,
  setSelectedChartId,
  clearChartsData,
  toggleHasIndependentConstraints,
} = SearchnFilterSlice.actions;

export default SearchnFilterSlice.reducer;

const SetSerchDetailsDirty = (state: any) => {
  if (!state.searchDetails.isDirty) {
    SearchnFilterSlice.caseReducers.setSerchDetailsDirty(state, {
      type: 'setSerchDetailsDirty',
      payload: true,
    });
  }
};

const targetRelationActionUtil = (
  relationIds: { [key: string]: number[] },
  state: any,
) => {
  const targetsDef = state.searchTargets.data;
  const targetRelationWithDetail: { [key: string]: SearchTargetDTO[] } = {};
  if (state.searchTargets.data.length) {
    for (const key in relationIds) {
      if (relationIds.hasOwnProperty(key)) {
        relationIds[key].forEach((targetId) => {
          if (!targetRelationWithDetail[key]) {
            targetRelationWithDetail[key] = [];
          }
          const st = targetsDef.find((t: SearchTargetDTO) => t.id === targetId);
          if (st) {
            targetRelationWithDetail[key].push(st);
          }
        });
      }
    }
    state.targetRelations.data = targetRelationWithDetail;
  }
  state.targetRelations.isFetching = false;
};

const getFilterType = (state: any, id: number): string => {
  return state.searchTargets.data.flatMap((t) => t.searchFields).find((f) => f.id === id)
    ?.fieldClass;
};

const targetRelationActionUtilOld = (
  relationIds: { [key: string]: number[] },
  state: any,
) => {
  const targetsDef = state.searchTargetsOld.data;
  const targetRelationWithDetail: { [key: string]: SearchTargetDTO[] } = {};
  if (state.searchTargetsOld.data.length) {
    for (const key in relationIds) {
      if (relationIds.hasOwnProperty(key)) {
        relationIds[key].forEach((targetId) => {
          if (!targetRelationWithDetail[key]) {
            targetRelationWithDetail[key] = [];
          }
          const st = targetsDef.find((t: SearchTargetDTO) => t.id === targetId);
          if (st) {
            targetRelationWithDetail[key].push(st);
          }
        });
      }
    }
    state.targetRelationsOld.data = targetRelationWithDetail;
  }
  state.targetRelationsOld.isFetching = false;
};
